import type { MapEdge, MapNode } from "@thalamos/aspire-common";

const TYPE_LABELS: Record<MapNode["type"], string> = {
  "nhs-trust": "NHS Trust",
  "nhs-hospital": "NHS Hospital",
  "local-authority": "Local Authority",
  police: "Police Unit",
  "place-of-safety": "Place of Safety",
  cqc: "CQC",
  "cqc-provider": "CQC Provider",
  independent: "Independent Doctor",
  prison: "Prison",
  "incident-location": "Incident Location",
  "combined-facility": "Hospital & Place of Safety",
  "gp-practice": "GP Practice",
  pcn: "Primary Care Network",
};

const TYPE_COLOURS: Record<MapNode["type"], string> = {
  "nhs-trust": "#6366f1",
  "nhs-hospital": "#38bdf8",
  "local-authority": "#14b8a6",
  police: "#22c55e",
  "place-of-safety": "#f59e0b",
  cqc: "#a855f7",
  "cqc-provider": "#ec4899",
  independent: "#8b5cf6",
  prison: "#64748b",
  "incident-location": "#ef4444",
  "combined-facility": "#38bdf8",
  "gp-practice": "#10b981",
  pcn: "#06b6d4",
};

interface NodeTooltipProps {
  node: MapNode;
  x: number;
  y: number;
  edges: MapEdge[];
  nodeMap: Map<string, MapNode>;
  mode?: "floating" | "topbar";
  onDismiss?: () => void;
}

function useTopNeighbours(
  node: MapNode,
  edges: MapEdge[],
  nodeMap: Map<string, MapNode>,
) {
  const neighbourWeights = new Map<string, number>();
  for (const e of edges) {
    if (e.sourceId === node.id) {
      neighbourWeights.set(
        e.targetId,
        (neighbourWeights.get(e.targetId) ?? 0) + e.weight,
      );
    } else if (e.targetId === node.id) {
      neighbourWeights.set(
        e.sourceId,
        (neighbourWeights.get(e.sourceId) ?? 0) + e.weight,
      );
    }
  }
  return [...neighbourWeights.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id, weight]) => ({
      name: nodeMap.get(id)?.name ?? id,
      weight,
    }));
}

function TopBarTooltip({
  node,
  edges,
  nodeMap,
  onDismiss,
}: Pick<NodeTooltipProps, "node" | "edges" | "nodeMap" | "onDismiss">) {
  const topNeighbours = useTopNeighbours(node, edges, nodeMap);
  const colour = TYPE_COLOURS[node.type];

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        background: `linear-gradient(135deg, ${colour}dd, ${colour}99)`,
        backdropFilter: "blur(12px)",
        padding: "10px 14px",
        color: "#fff",
        fontSize: 13,
        lineHeight: 1.5,
        zIndex: 70,
        boxShadow: `0 4px 20px ${colour}40`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{node.name}</div>
          <div
            style={{
              fontSize: 11,
              opacity: 0.85,
              marginBottom: 2,
            }}
          >
            {TYPE_LABELS[node.type]}
            {node.parentName ? ` · ${node.parentName}` : ""}
          </div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>
            Volume: <strong>{node.volume}</strong>
            {topNeighbours.length > 0 && (
              <>
                {" · "}
                {topNeighbours
                  .slice(0, 3)
                  .map((n) => n.name)
                  .join(", ")}
              </>
            )}
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              fontSize: 16,
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: "pointer",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export function NodeTooltip({
  node,
  x,
  y,
  edges,
  nodeMap,
  mode = "floating",
  onDismiss,
}: NodeTooltipProps) {
  // Called before the mode branch so the hook runs unconditionally every render.
  const topNeighbours = useTopNeighbours(node, edges, nodeMap);

  if (mode === "topbar") {
    return (
      <TopBarTooltip
        node={node}
        edges={edges}
        nodeMap={nodeMap}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        left: x + 12,
        top: y - 12,
        background: "rgba(15, 23, 42, 0.95)",
        border: `1px solid ${TYPE_COLOURS[node.type]}`,
        borderRadius: 8,
        padding: "10px 14px",
        color: "#e2e8f0",
        fontSize: 13,
        lineHeight: 1.5,
        pointerEvents: "none",
        zIndex: 50,
        maxWidth: 300,
        whiteSpace: "nowrap",
        boxShadow: `0 0 12px ${TYPE_COLOURS[node.type]}40`,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
        {node.name}
      </div>
      <div
        style={{
          color: TYPE_COLOURS[node.type],
          fontSize: 11,
          marginBottom: 6,
        }}
      >
        {TYPE_LABELS[node.type]}
      </div>
      {node.parentName && (
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 4 }}>
          Part of {node.parentName}
        </div>
      )}
      {node.postcode && (
        <div style={{ color: "#64748b", fontSize: 10, marginBottom: 4 }}>
          Postcode: {node.postcode}
        </div>
      )}
      {node.memberNames && node.memberNames.length > 1 && (
        <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 4 }}>
          {node.memberNames.map((name) => (
            <div key={name}>• {name}</div>
          ))}
        </div>
      )}
      <div style={{ color: "#94a3b8", fontSize: 12 }}>
        Activity volume:{" "}
        <strong style={{ color: "#e2e8f0" }}>{node.volume}</strong>
      </div>
      {topNeighbours.length > 0 && (
        <>
          <div
            style={{
              color: "#64748b",
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginTop: 8,
              marginBottom: 4,
            }}
          >
            Top connections
          </div>
          {topNeighbours.map((n, index) => (
            <div
              key={`${n.name}-${index}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                fontSize: 11,
                color: "#94a3b8",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {n.name}
              </span>
              <span style={{ color: "#e2e8f0", flexShrink: 0 }}>
                {n.weight}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
